/**
 * DepositStatusLine - Small status line showing deposit status on estimates
 * 
 * Display states:
 * - Paid: "Deposit paid: $X" (green) - when deposit_paid=true OR deposit_status=PAID
 * - Requested + unpaid: "Deposit: $X — Awaiting payment" (amber) + "Deposit Paid" button
 * - Not enabled: nothing shown
 * 
 * P0 FIX: Now checks deposit_paid boolean AND deposit_paid_cents for single-document deposits
 * P0 FIX: "Deposit Paid" button available for ALL contractors (with or without payment processing)
 */

import { CurrencyDollar, CheckCircle, Clock, CreditCard } from "phosphor-react";
import { formatCurrency } from "../../utils/formatters";
import { Button } from "../ui/button";

export default function DepositStatusLine({ 
  estimate, 
  onMarkReceived,
  showMarkReceivedAction = false,
}) {
  // Don't show anything if deposit is not enabled or configured
  if (!estimate?.deposit_enabled) return null;
  
  const depositStatus = estimate.deposit_status || "NONE";
  const depositAmount = calculateDepositAmount(estimate);
  
  // P0 FIX: Check for single-document deposit payment fields
  const depositPaid = estimate.deposit_paid === true;
  const depositPaidCents = estimate.deposit_paid_cents || 0;
  
  // Determine if deposit is paid using BOTH legacy status AND new fields
  const isDepositPaid = depositPaid || depositStatus === "PAID" || depositStatus === "paid";
  
  // Don't show if no deposit amount calculated AND no deposit paid
  if ((!depositAmount || depositAmount <= 0) && !depositPaidCents) return null;
  
  // PAID state - use actual paid amount if available, otherwise calculated amount
  if (isDepositPaid) {
    const displayAmount = depositPaidCents > 0 ? (depositPaidCents / 100) : depositAmount;
    return (
      <div 
        className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"
        data-testid="deposit-status-paid"
      >
        <CheckCircle size={16} className="text-emerald-600" weight="fill" />
        <span className="text-emerald-700">
          Deposit paid: {formatCurrency(displayAmount)}
        </span>
      </div>
    );
  }
  
  // REQUESTED state (awaiting payment) - show gold-standard "Deposit Paid" button
  if (depositStatus === "REQUESTED") {
    return (
      <div 
        className="flex items-center justify-between gap-2 text-sm bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
        data-testid="deposit-status-requested"
      >
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-amber-600" />
          <span className="text-amber-700">
            Deposit: {formatCurrency(depositAmount)} — <span className="font-medium">Awaiting payment</span>
          </span>
        </div>
        {showMarkReceivedAction && onMarkReceived && (
          <Button
            size="sm"
            variant="outline"
            onClick={onMarkReceived}
            className="h-7 px-3 text-xs font-medium text-amber-700 border-amber-300 hover:bg-amber-100 hover:text-amber-800"
            data-testid="mark-deposit-paid-btn"
          >
            <CreditCard size={14} className="mr-1" />
            Deposit Paid
          </Button>
        )}
      </div>
    );
  }
  
  // NONE state (deposit configured but not yet requested - estimate not approved yet)
  // Also show for approved status if deposit is somehow still in NONE state
  return (
    <div 
      className="flex items-center gap-2 text-sm bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2"
      data-testid="deposit-status-configured"
    >
      <CurrencyDollar size={16} className="text-zinc-500" />
      <span className="text-zinc-600">
        Deposit required: {formatCurrency(depositAmount)} 
        {estimate.status !== "approved" && estimate.status !== "accepted" && (
          <span className="text-zinc-400"> (due on approval)</span>
        )}
      </span>
    </div>
  );
}

/**
 * Calculate the deposit amount from estimate data
 */
function calculateDepositAmount(estimate) {
  if (!estimate?.deposit_enabled) return 0;
  
  const total = estimate.total || 0;
  const depositType = estimate.deposit_type;
  const depositValue = estimate.deposit_value;
  
  if (!depositValue || depositValue <= 0) return 0;
  
  if (depositType === "PERCENT") {
    return (total * depositValue) / 100;
  } else if (depositType === "FIXED") {
    return Math.min(depositValue, total);
  }
  
  return 0;
}
